import { FF_ENABLE_PMS_ERROR_HANDLING } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import usePutCancelPrescribedProducts from '@/hooks/prescribedProducts/usePutCancelPrescribedProducts';
import type { Logger } from '@/utils/logger';
import toast from '@montugroup/design-system/Toast';
import { useState } from 'react';

export const useCancelProduct = ({ patientId, logger }: { patientId?: number; logger?: Logger }) => {
  const { mutateAsync, reset, isPending } = usePutCancelPrescribedProducts();
  const { flags } = useFeatureFlags();
  const ffEnableErrorHandling = flags[FF_ENABLE_PMS_ERROR_HANDLING];

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    productId?: number;
    productName?: string;
  }>({
    isOpen: false
  });

  const cancelProduct = async (id: number) => {
    try {
      const response = await mutateAsync({
        params: { patientId: patientId?.toString() ?? '' },
        body: { productIds: [id] }
      });

      if (response.status === 200) {
        toast.success({
          message: `ePrescription cancelled successfully`
        });
      }
    } catch (error) {
      if (!ffEnableErrorHandling) {
        logger?.error(`Failed to cancel product`, error);
        toast.error({
          message: `Cancel prescription failed`
        });
      }

      reset();
    }
  };

  const handleCancelProduct = (productId: number, productName: string) => {
    setModalState({
      isOpen: true,
      productId,
      productName
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      productId: undefined,
      productName: undefined
    });
  };

  const confirmCancelProduct = () => {
    if (modalState.productId) {
      cancelProduct(modalState.productId);
    }

    closeModal();
  };

  return {
    handleCancelProduct,
    cancelInProgress: isPending,
    modalState,
    closeModal,
    confirmCancelProduct
  };
};
